import axios from "axios";
import { api } from "./_client";

export async function analyzeProject(payload, onStage, onJobId) {
    // Analyze runs as a background job (fixes Cloudflare 100s and preview
    // 60s ingress timeouts). POST starts it, then we poll until done. An
    // optional onStage({stage, progress_pct}) callback is fired on every
    // poll so the UI can render a live "what it's doing" bar. ``onJobId``
    // receives the job id the moment the server accepts the request — so
    // the UI can wire a Cancel button before the first poll returns.
    const { data: job } = await api.post(`/analyze`, payload);
    onJobId && onJobId(job.id);
    return pollAiJob(job.id, { onStage });
}

export async function createTakeoff(payload, onStage, onJobId) {
    const { data: job } = await api.post(`/takeoff`, payload);
    const jobId = job.job_id || job.id;
    onJobId && onJobId(jobId);
    return pollAiJob(jobId, { onStage });
}

export async function listTakeoffItems(projectId) {
    const { data } = await api.get(`/takeoff/items`, { params: { project_id: projectId } });
    return data;
}

export async function updateTakeoffItem(id, patch) {
    const { data } = await api.patch(`/takeoff/items/${id}`, patch);
    return data;
}

export async function deleteTakeoffItem(id) {
    const { data } = await api.delete(`/takeoff/items/${id}`);
    return data;
}

export async function restoreTakeoffItem(snapshot) {
    const { data } = await api.post(`/takeoff/items/restore`, snapshot);
    return data;
}

export async function createTakeoffItem(payload) {
    const { data } = await api.post(`/takeoff/items`, payload);
    return data;
}

export async function aiAssistTakeoffItem(id) {
    const { data } = await api.post(`/takeoff/items/${id}/ai-assist`);
    return data;
}

// Shared internal helper — exported so other api/* modules
// (estimates.js, templates.js) can reuse it.
// iter-110: the previous 30-min hard cap was killing large estimate
// jobs mid-flight (users watched them tick past page 90/127 then hit
// "Job timed out"). There's no reason to timeout on the client — the
// backend job runs on its own with cancel support, and the user can
// hit Cancel any time. Poll indefinitely until the backend reports
// done/error/canceled.
//
// iter-202: Network-blip survival. The backend job keeps running on
// its own asyncio task even if the browser loses connectivity. We
// MUST NOT let an axios "Network Error" during polling bubble up as
// a fatal estimate failure. Any transient fetch error (no response,
// 502/503/504, timeout, CDN cold-start) is retried with exponential
// backoff — effectively forever, until a real HTTP status comes back
// (which is where we read done/error/canceled).
function _isTransientPollError(err) {
    if (!err) return false;
    // Pure network drop — axios returns Error with no `.response`.
    if (!err.response) return true;
    const status = err.response?.status;
    if (status && [408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524].includes(status)) {
        return true;
    }
    const msg = (err.message || "").toLowerCase();
    if (msg.includes("network error") || msg.includes("timeout") || msg.includes("timed out")
        || msg.includes("fetch") || msg.includes("err_network") || msg.includes("err_internet")) {
        return true;
    }
    return false;
}

export async function pollAiJob(jobId, { intervalMs = 2000, onStage } = {}) {
    // Small head-start so quick jobs can resolve on the first poll.
    await new Promise((r) => setTimeout(r, 1000));
    const started = Date.now();
    let transientRetries = 0; // consecutive network-level failures
    while (true) {
        let data;
        try {
            const resp = await api.get(`/ai-jobs/${jobId}`);
            data = resp.data;
            transientRetries = 0; // reset on any good response
        } catch (err) {
            // 404 = job really doesn't exist; stop.
            if (err?.response?.status === 404) {
                throw new Error("Job not found");
            }
            if (_isTransientPollError(err)) {
                transientRetries += 1;
                // Exponential backoff cap 30s. Fire a status event so
                // the UI can show "Reconnecting…" if it wants, but we
                // never give up.
                if (typeof onStage === "function") {
                    onStage({
                        stage: `Reconnecting (attempt ${transientRetries})…`,
                        progress_pct: null,
                        status: "reconnecting",
                        usage: null,
                    });
                }
                const backoff = Math.min(2_000 * Math.pow(1.5, Math.min(transientRetries, 10)), 30_000);
                await new Promise((r) => setTimeout(r, backoff));
                continue;
            }
            // Non-transient — propagate (401, 403, etc.)
            throw err;
        }
        if (typeof onStage === "function") {
            onStage({
                stage: data.stage || "",
                progress_pct: typeof data.progress_pct === "number" ? data.progress_pct : null,
                status: data.status,
                // Live iQ counter — { calls, input_tokens, output_tokens, cost_usd, model }
                usage: data.usage || null,
                // iter-214 — page scan counter (extraction phase)
                pages_scanned: typeof data.pages_scanned === "number" ? data.pages_scanned : null,
                pages_total: typeof data.pages_total === "number" ? data.pages_total : null,
            });
        }
        if (data.status === "done") {
            if (!data.result) throw new Error("Job completed without a result");
            return data.result;
        }
        if (data.status === "canceled") {
            const err = new Error("Canceled by user");
            err.response = { data: { detail: "Canceled by user" } };
            err.canceled = true;
            throw err;
        }
        if (data.status === "error") {
            const err = new Error(data.error || "Job failed");
            // Shape like an axios error so existing call-sites keep working.
            err.response = { data: { detail: data.error || "Job failed" } };
            // iter-202 — flag context-overflow terminal errors so the
            // UI layer can auto-retry transparently instead of asking
            // the user to click retry.
            const errMsg = (data.error || "").toLowerCase();
            if (errMsg.includes("context") || errMsg.includes("compacting") || errMsg.includes("token")) {
                err.contextOverflow = true;
            }
            throw err;
        }
        // iter-203 — "interrupted" means the backend got killed mid-run
        // (uvicorn reload / supervisor restart). Our server-side
        // watchdog will auto-resume within ~90s. Treat it exactly like
        // "running" and keep polling — DO NOT bubble it up as an error.
        if (data.status === "interrupted") {
            // optional: show a friendly stage message via onStage above.
        }
        // Adaptive back-off: 2s → 5s after 5min → 10s after 30min. No
        // hard timeout — a 500-page million-dollar estimate can run for
        // hours and the poll loop stays healthy.
        const elapsedMs = Date.now() - started;
        const wait =
            elapsedMs < 5 * 60_000 ? intervalMs
                : elapsedMs < 30 * 60_000 ? 5_000
                    : 10_000;
        await new Promise((r) => setTimeout(r, wait));
    }
}

export async function cancelAiJob(jobId) {
    const { data } = await api.post(`/ai-jobs/${jobId}/cancel`);
    return data;
}

export async function getAiJob(id) {
    const { data } = await api.get(`/ai-jobs/${id}`);
    return data;
}

// iter-202 — Returns the latest in-flight AI job for a project (if
// any). Used by the frontend on WorkspacePage mount so a user who
// reloaded during a running estimate re-attaches to the SAME job
// instead of starting a new one and losing all progress.
export async function getActiveAiJob(projectId, kind = null) {
    const params = { project_id: projectId };
    if (kind) params.kind = kind;
    const { data } = await api.get(`/ai-jobs/active`, { params });
    return data?.job || null;
}

export async function listAnalyses(projectId) {
    const { data } = await api.get(`/analyses`, { params: { project_id: projectId } });
    return data;
}

export async function deleteAnalysis(id) {
    const { data } = await api.delete(`/analyses/${id}`);
    return data;
}

// --- Templates (price database) ---
